import { useMemo } from 'react';
import { FindAllMyProjectsResponse } from '@ansvar-sakerhet/api-client';
import { Props } from '../ProjectsTable';
import { SortControls } from '../../../../../../../traits/api/projects/useGetMyProjects';
import { ActionsCell, ProjectRowTarget } from '../components/ActionsCell';

export const useMyProjectsTableData = (
  projects: FindAllMyProjectsResponse[],
  canManageProject: boolean,
  onEdit: (target: ProjectRowTarget) => void,
  onDelete: (target: ProjectRowTarget) => void
) => {
  const columns = useMemo(() => {
    return [
      {
        Header: 'Namn',
        accessor: 'name',
        onClick: (setSort: Props['setSort'], currentSort: SortControls) => {
          if (currentSort === SortControls.ProjectNameAsc) {
            return setSort(SortControls.ProjectNameDesc);
          }

          return setSort(SortControls.ProjectNameAsc);
        },
        iconSrc: (currentSort: SortControls) =>
          currentSort === SortControls.ProjectNameAsc
            ? 'ArrowDropUp'
            : 'ArrowDropDown',
      },
      {
        Header: 'Huvudentreprenör',
        accessor: 'mainContractor',
        onClick: (setSort: Props['setSort'], currentSort: SortControls) => {
          if (currentSort === SortControls.MainContractorAsc) {
            return setSort(SortControls.MainContractorDesc);
          }

          return setSort(SortControls.MainContractorAsc);
        },
        iconSrc: (currentSort: SortControls) =>
          currentSort === SortControls.MainContractorAsc
            ? 'ArrowDropUp'
            : 'ArrowDropDown',
      },
      {
        Header: 'Platschef',
        accessor: 'siteManager',
        onClick: (setSort: Props['setSort'], currentSort: SortControls) => {
          if (currentSort === SortControls.SiteManagerAsc) {
            return setSort(SortControls.SiteManagerDesc);
          }

          return setSort(SortControls.SiteManagerAsc);
        },
        iconSrc: (currentSort: SortControls) =>
          currentSort === SortControls.SiteManagerAsc
            ? 'ArrowDropUp'
            : 'ArrowDropDown',
      },
      {
        Header: 'Antal ej åtgärdade avvikelser',
        accessor: 'deviationsRemaining',
        onClick: null,
        iconSrc: null,
      },
      ...(canManageProject
        ? [
            {
              Header: '',
              accessor: 'projectId',
              onClick: null,
              iconSrc: null,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              Cell: ({ row }: any) => (
                <ActionsCell
                  projectId={row.original.projectId}
                  name={row.original.name}
                  canDelete={row.original.canDelete}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ),
            },
          ]
        : []),
      // The trailing actions column mixes a plain accessor with a custom
      // Cell renderer, which react-table's Column<D> union type can't
      // narrow together with the sort-header columns above.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ] as any;
  }, [canManageProject, onEdit, onDelete]);

  const data = useMemo(() => {
    return projects.map((project) => {
      return {
        name: project.name,
        mainContractor: project.mainContractor,
        siteManager: project.siteManager,
        deviationsRemaining: `${project.deviationsRemaining ?? 0} st`,
        projectId: project.projectId,
        canDelete: project.canDelete,
      };
    });
  }, [projects]);

  return { columns, data };
};
